'use client';

import React, { Fragment, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery } from '@apollo/client';
import Header from '../../components/Header';
import Load from '../../components/Load';
import TournamentOrganizerItemView from './organizer/components/TournamentOrganizerItemView';
import SelectUserOrganizerModal from '../../components/liga/SelectUserOrganizerModal';
import { type OrganizadorFuncao } from '../../validation/tournamentOrganizerSchema';
import {
  ORGANIZADORES_POR_TORNEIO,
  OrganizadoresPorTorneioData,
  OrganizadoresPorTorneioVars,
  criarOrganizador,
  removerOrganizador,
  Organizador,
} from '../../services/organizadorService';
import {
  TORNEIO_POR_ID,
  TorneioPorIdData,
  TorneioPorIdVars,
} from '../../services/torneioService';

interface OrganizerItem extends Omit<Organizador, 'id'> {
  id?: string;
  tempId: string;
}

const AdminTournamentOrganizerPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const torneioId = searchParams.get('torneioId');

  const { data: torneioData, loading: loadingTorneio } = useQuery<
    TorneioPorIdData,
    TorneioPorIdVars
  >(TORNEIO_POR_ID, {
    variables: { id: torneioId || '' },
    skip: !torneioId,
  });

  const {
    data: organizadoresData,
    loading: loadingOrganizadores,
    refetch,
  } = useQuery<OrganizadoresPorTorneioData, OrganizadoresPorTorneioVars>(
    ORGANIZADORES_POR_TORNEIO,
    {
      variables: { torneioId: torneioId || '' },
      skip: !torneioId,
    },
  );

  const [items, setItems] = useState<OrganizerItem[]>([]);
  const [deletingMap, setDeletingMap] = useState<Record<string, boolean>>({});
  const [saving, setSaving] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);

  useEffect(() => {
    if (organizadoresData?.organizadoresPorTorneio) {
      const mapped = organizadoresData.organizadoresPorTorneio.map((o) => ({
        ...o,
        tempId: o.id,
      }));
      setItems(mapped);
    }
  }, [organizadoresData]);

  const handleAddClick = () => {
    setShowAddModal(true);
  };

  const handleAddOrganizer = async (
    usuarioId: string,
    nomeUsuario: string,
    nomeFuncao: OrganizadorFuncao,
  ) => {
    if (!torneioId) return;
    setSaving(true);
    try {
      const created = await criarOrganizador({
        torneioId: torneioId,
        usuarioId: usuarioId,
        nomeFuncao: nomeFuncao,
        nome: nomeUsuario, // Nome do usuário selecionado
        telefone: null,
        fotoUrl: null,
      });
      if (created) {
        await refetch();
        alert('Organizador adicionado com sucesso');
      }
    } catch (e) {
      alert('Erro ao adicionar organizador');
    } finally {
      setSaving(false);
      setShowAddModal(false);
    }
  };

  const handleDelete = async (item: OrganizerItem) => {
    const nomeUsuario = item.usuario?.nome || item.nome || 'este organizador';

    // Verificar se é o único administrador
    const administradores = items.filter(
      (i) => i.nomeFuncao === 'ADMINISTRADOR' && i.id,
    );
    const isUnicoAdministrador =
      item.nomeFuncao === 'ADMINISTRADOR' &&
      administradores.length === 1 &&
      administradores[0].id === item.id;

    if (isUnicoAdministrador) {
      alert(
        'Não é possível remover o único administrador do torneio. Deve haver pelo menos um organizador com função de Administrador.',
      );
      return;
    }

    if (!window.confirm(`Remover organizador ${nomeUsuario}?`)) return;

    if (!item.id) {
      return;
    }

    setDeletingMap((prev) => ({ ...prev, [item.tempId]: true }));
    try {
      await removerOrganizador(item.id);
      await refetch();
      alert('Organizador removido com sucesso');
    } catch (e) {
      alert('Erro ao remover organizador');
    } finally {
      setDeletingMap((prev) => {
        const { [item.tempId]: _removed, ...rest } = prev;
        return rest;
      });
    }
  };

  const canDeleteOrganizer = (item: OrganizerItem): boolean => {
    // Verificar se é o único administrador
    const administradores = items.filter(
      (i) => i.nomeFuncao === 'ADMINISTRADOR' && i.id,
    );
    const isUnicoAdministrador =
      item.nomeFuncao === 'ADMINISTRADOR' &&
      administradores.length === 1 &&
      administradores[0].id === item.id;

    return !isUnicoAdministrador;
  };

  if (loadingTorneio || loadingOrganizadores) return <Load />;

  const torneio = torneioData?.torneioPorId;

  return (
    <Fragment>
      <div className="main-wrapper">
        <Header />
        <div className="main-content theme-dark-bg right-chat-active">
          <div className="middle-sidebar-bottom">
            <div className="middle-sidebar-left">
              <div className="middle-wrap">
                <div className="card w-100 border-0 bg-white shadow-xs p-0 mb-4">
                  <div className="card-body p-4 w-100 bg-current border-0 d-flex rounded-3 align-items-center">
                    <button
                      type="button"
                      className="btn btn-link text-white p-0 d-inline-block mt-2 me-4"
                      onClick={() => window.history.back()}
                    >
                      <i className="ti-arrow-left font-sm"></i>
                    </button>
                    <h4 className="font-xs text-white fw-600 mb-0 mt-2 flex-grow-1">
                      {torneio?.nome || 'Torneio'}
                    </h4>
                  </div>
                </div>

                <div className="mb-3">
                  {torneio?.bannerUrl && (
                    <img
                      src={torneio.bannerUrl}
                      alt="banner"
                      className="w-100 rounded"
                      style={{ maxHeight: 200, objectFit: 'cover' }}
                    />
                  )}
                </div>

                <div className="d-flex justify-content-end mb-3">
                  <button
                    className="btn btn-secondary"
                    onClick={handleAddClick}
                    disabled={saving}
                  >
                    Novo organizador +
                  </button>
                </div>

                <div className="row g-3">
                  {items.map((item) => {
                    const canDelete = canDeleteOrganizer(item);
                    return (
                      <div key={item.tempId} className="col-12">
                        <TournamentOrganizerItemView
                          organizador={item}
                          onDelete={canDelete ? () => void handleDelete(item) : undefined}
                          busy={saving || !!deletingMap[item.tempId]}
                          readOnly={false}
                          disableDeleteReason={
                            !canDelete && item.nomeFuncao === 'ADMINISTRADOR'
                              ? 'Não é possível remover o único administrador do torneio'
                              : undefined
                          }
                        />
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <SelectUserOrganizerModal
        open={showAddModal}
        onClose={() => setShowAddModal(false)}
        onSelect={handleAddOrganizer}
        existingOrganizerUserIds={items
          .filter((item) => item.usuario?.id)
          .map((item) => item.usuario!.id)}
      />
    </Fragment>
  );
};

export default AdminTournamentOrganizerPage;
